import { firestore } from '@src/api/Firebase/Firebase';
import { collection } from 'firebase/firestore';
import { ExpenseGroup } from '@api/CashFlows/CashFlow';

// ============================================================================
// CATEGORÍAS DE GASTO — `expense-categories` (PLAN-GASTOS-Y-ADMIN-DE-SUCURSAL §3.3).
//
// Cada categoría pertenece a un GRUPO, que es un renglón del Estado de Resultados.
// Solo el admin general las administra. **Desactivar, nunca borrar**: cada gasto
// guarda copia del nombre y del grupo, así que renombrar no reescribe la historia.
//
// Lecturas: ~25 docs una vez por sesión (`ExpenseCategoryServ`, caché en memoria).
// ============================================================================

export interface ExpenseCategory {
    id: string;
    name: string;
    group: ExpenseGroup;
    active: boolean;
    /** Orden dentro de su grupo. */
    order: number;
    /** Nómina: pide el colaborador al que se le paga (usuarios activos de la sucursal). */
    askCollaborator?: boolean;
    /** Compra de insumo: pide el insumo de la lista (`inputs`), no texto libre. */
    askInput?: boolean;
    /** Cómo arranca el interruptor "Salió de la caja de este turno". Ausente = encendido. */
    paidFromCashByDefault?: boolean;
    /** Se guarda como `CashFlowType.Shipping`: el informe tiene su propio renglón de envíos. */
    recordsAsShipping?: boolean;
}

export interface ExpenseGroupInfo {
    group: ExpenseGroup;
    name: string;
    /** Orden del renglón en el Estado de Resultados. */
    order: number;
}

/**
 * Los grupos, en el orden del Estado de Resultados. Decisión del negocio (§8.1,
 * 2026-09-27): **variable** = servicios que llegan cada mes con monto distinto (luz,
 * agua, gas); **operativo** = lo del día (basura, mandados, Uber).
 */
export const EXPENSE_GROUPS: ExpenseGroupInfo[] = [
    { group: ExpenseGroup.Supplies, name: 'Insumos', order: 1 },
    { group: ExpenseGroup.Payroll, name: 'Nómina', order: 2 },
    { group: ExpenseGroup.Fixed, name: 'Gastos fijos', order: 3 },
    { group: ExpenseGroup.Variable, name: 'Gastos variables', order: 4 },
    { group: ExpenseGroup.Maintenance, name: 'Mantenimiento', order: 5 },
    { group: ExpenseGroup.Operating, name: 'Gasto operativo', order: 6 },
    { group: ExpenseGroup.OutsideStatement, name: 'Fuera del estado de resultados', order: 7 }
];

export const getExpenseGroupName = (group?: ExpenseGroup): string =>
    EXPENSE_GROUPS.find((info) => info.group === group)?.name || 'Sin categoría';

type SeedRow = [id: string, name: string, extra?: Partial<ExpenseCategory>];

const seedGroup = (group: ExpenseGroup, rows: SeedRow[]): ExpenseCategory[] =>
    rows.map(([id, name, extra], index) => ({ id, name, group, active: true, order: index + 1, ...extra }));

const NOT_FROM_CASH: Partial<ExpenseCategory> = { paidFromCashByDefault: false };

/**
 * La semilla, sacada de lo que capturan de verdad (60 días de descripciones, §2.2).
 * **Ids fijos**: si dos pantallas siembran a la vez, escriben el mismo documento con
 * lo mismo y no hace falta transacción. Una vez sembrado, manda Firestore.
 */
export const EXPENSE_CATEGORY_SEED: ExpenseCategory[] = [
    ...seedGroup(ExpenseGroup.Supplies, [
        ['supplies-input', 'Compra de insumo', { askInput: true }],
        ['supplies-other', 'Otro insumo (no está en la lista)']
    ]),
    // Nómina arranca "con otro dinero" (2026-10-08): los 10 pagos capturados del 30-sep al 8-oct
    // superaron el efectivo que entró a la caja ese día (el lote del 1-oct, $16,930, con $1,027 de
    // ventas en efectivo); marcados "de la caja", dejaron el corte en -$14,903.
    ...seedGroup(ExpenseGroup.Payroll, [['payroll', 'Nómina', { askCollaborator: true, ...NOT_FROM_CASH }]]),
    ...seedGroup(ExpenseGroup.Fixed, [
        ['fixed-rent', 'Renta', NOT_FROM_CASH],
        ['fixed-internet', 'Internet y teléfono', NOT_FROM_CASH],
        ['fixed-other', 'Otros fijos', NOT_FROM_CASH]
    ]),
    ...seedGroup(ExpenseGroup.Variable, [
        ['variable-power', 'Luz', NOT_FROM_CASH],
        ['variable-water', 'Agua (servicio)', NOT_FROM_CASH],
        ['variable-gas', 'Gas'],
        ['variable-other', 'Otros variables']
    ]),
    ...seedGroup(ExpenseGroup.Maintenance, [
        ['maintenance-plumbing', 'Plomería'],
        ['maintenance-repairs', 'Reparaciones'],
        ['maintenance-equipment', 'Equipo']
    ]),
    ...seedGroup(ExpenseGroup.Operating, [
        ['operating-trash', 'Recolección de basura'],
        ['operating-errands', 'Uber y mandados'],
        ['operating-shipping', 'Envío', { recordsAsShipping: true }],
        ['operating-cleaning', 'Limpieza'],
        ['operating-stationery', 'Papelería'],
        ['operating-fuel', 'Combustible'],
        ['operating-other', 'Otros operativos']
    ]),
    ...seedGroup(ExpenseGroup.OutsideStatement, [
        ['outside-tips', 'Propinas entregadas'],
        ['outside-adjustment', 'Ajuste de caja']
    ])
];

/** Activas, en el orden del ER y luego el del grupo. Lo que ve quien captura. */
export const sortExpenseCategories = (categories: ExpenseCategory[]): ExpenseCategory[] => {
    const groupOrder = (group: ExpenseGroup) => EXPENSE_GROUPS.find((info) => info.group === group)?.order ?? 99;
    return [...categories].sort(
        (a, b) => groupOrder(a.group) - groupOrder(b.group) || a.order - b.order || a.name.localeCompare(b.name)
    );
};

export const EXPENSE_CATEGORIES_COLLECTION = 'expense-categories';
export const expenseCategoriesRef = collection(firestore, EXPENSE_CATEGORIES_COLLECTION);
